import { Component, ChangeDetectionStrategy, computed, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { CmsContentType, CmsPicture } from '@fcom/core-api';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import {
  Media,
  MediaPicture,
  MediaType,
  MediaVideo,
  PhraseGroup,
  TravelGuide,
} from '../../interfaces/travel-guide.interface';
import { removeEmptyCmsText } from '../../services/cms-utils/cms-utils';

const isVideo = (o: Media): o is MediaVideo => o.type === 'video' && !!o.url;

type TravelGuideMediaSource = {
  media?: unknown;
};

const extractMedia: (data: TravelGuideMediaSource) => Media = (data = {}) => {
  if (!Array.isArray(data.media)) {
    return undefined;
  }

  const media = data.media as Array<{ contentType: CmsContentType; dataUrl?: string }>;
  const video = media.find((m) => m.contentType === CmsContentType.CMVideo);
  const picture = media.find((m) => m.contentType === CmsContentType.CMPicture);

  if (video) {
    return {
      type: 'video',
      url: video.dataUrl,
    } as MediaVideo;
  } else if (picture) {
    return {
      type: 'picture',
      picture,
    } as MediaPicture;
  }

  return undefined;
};

const mapTravelGuide: (data: TravelGuide) => TravelGuide = (data) => ({
  ...data,
  title: data.teaserTitle,
  text: removeEmptyCmsText(data.detailText),
  media: extractMedia(data),
  localSettings: data.localSettings || {},
});

@Component({
  selector: 'fin-travel-guide',
  templateUrl: './travel-guide.component.html',
  styleUrls: ['./travel-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TravelGuideComponent {
  private themeService = inject(FinnairThemeService);

  content = input<TravelGuide>();
  showLabel = input<boolean>(false);
  showHeading = input<boolean>(false);

  public readonly DOCTYPE: typeof CmsContentType = CmsContentType;

  readonly isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  private readonly outputContent = computed<TravelGuide | undefined>(() => {
    const content = this.content();
    return content ? mapTravelGuide(content) : undefined;
  });

  readonly title = computed<string | undefined>(() => this.outputContent()?.title);

  readonly text = computed<string | undefined>(() => this.outputContent()?.text);

  readonly mediaType = computed<MediaType | undefined>(() => this.outputContent()?.media?.type);

  readonly videoUrl = computed<string | undefined>(() => {
    const media = this.outputContent()?.media;
    return media && isVideo(media) ? media.url : undefined;
  });

  readonly mediaPicture = computed<CmsPicture | undefined>(() => {
    const media = this.outputContent()?.media;
    return media && media.type === 'picture' ? media.picture : undefined;
  });

  readonly phraseGroups = computed<PhraseGroup[] | undefined>(() => this.outputContent()?.localSettings?.commonPhrases);

  readonly videoUrl$ = toObservable(this.videoUrl);
  readonly videoTitle$ = toObservable(this.title);
  readonly mediaPicture$ = toObservable(this.mediaPicture);
}
